import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NavBar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'backdrop-blur-lg' : ''
      }`}
      style={{ 
        backgroundColor: isScrolled ? 'rgba(5, 10, 20, 0.95)' : 'transparent',
        borderBottom: isScrolled ? '1px solid rgba(255, 255, 255, 0.04)' : 'none'
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="flex items-center gap-3"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_metalove-preview/artifacts/hs618z4m_Logo%20ML%20dore%CC%81.png"
              alt="MetaLove"
              className="h-7 w-auto"
              style={{ filter: 'brightness(0.5) sepia(0.1)', opacity: 0.6 }}
            />
            <span 
              className="font-heading text-lg tracking-[0.2em]"
              style={{ color: '#9CA3AF' }}
            >
              METALOVE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <Link 
              to="/agence"
              data-testid="nav-agency"
              className="nav-link"
              style={{ color: isActive('/agence') ? '#D1D5DB' : '#6B7280' }}
            >
              {t('nav.agency')}
            </Link>
            <Link 
              to="/postuler"
              data-testid="nav-apply"
              className="nav-link"
              style={{ color: isActive('/postuler') ? '#D1D5DB' : '#6B7280' }}
            >
              {t('nav.apply')}
            </Link>
            <Link 
              to="/bibliotheque"
              data-testid="nav-library"
              className="nav-link"
              style={{ color: isActive('/bibliotheque') ? '#D1D5DB' : '#6B7280' }}
            >
              {t('nav.library')}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-6">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              className="flex items-center gap-2 text-xs tracking-[0.15em]"
            >
              <span style={{ color: language === 'fr' ? '#D1D5DB' : '#6B7280' }}>FR</span>
              <span style={{ color: '#4B5563' }}>/</span>
              <span style={{ color: language === 'en' ? '#D1D5DB' : '#6B7280' }}>EN</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 transition-colors"
              style={{ color: '#6B7280' }}
            >
              {isMobileMenuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden mt-6 py-8 animate-fade-in"
            style={{ borderTop: '1px solid rgba(255, 255, 255, 0.04)' }}
          >
            <div className="flex flex-col gap-6">
              <Link 
                to="/agence"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-agency"
                className="nav-link text-left py-2"
              >
                {t('nav.agency')}
              </Link>
              <Link 
                to="/postuler"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-apply"
                className="nav-link text-left py-2"
              >
                {t('nav.apply')}
              </Link>
              <Link 
                to="/bibliotheque"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-library"
                className="nav-link text-left py-2"
              >
                {t('nav.library')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;
